class CustomColorPicker extends HTMLElement {
  constructor() {
    super();
    this.colorState = { hue: 0, lightness: 50 };
    this.activeTrack = null;
    this.observedImages = new WeakSet();
  }

  connectedCallback() {
    this.initComponent();

    // 1. Re-verify form inputs, re-observe media nodes, and sync underlay colors on variant mutations
    this.observer = new MutationObserver(() => {
      this.ensureHiddenInputExists();
      this.renderColorSystem();
      this.observeProductImages();
    });

    const targetSection = this.closest('section');
    if (targetSection) {
      this.observer.observe(targetSection, { childList: true, subtree: true });
    }

    // 2. Initialize the intersection tracker
    this.initImageObserver();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.imageObserver) this.imageObserver.disconnect();
  }

  initComponent() {
    this.targetFormId = this.getAttribute('data-form-id');
    this.hueTrack = this.querySelector('#hue-slider');
    this.hueHandle = this.querySelector('#hue-handle');
    this.lightTrack = this.querySelector('#lightness-slider');
    this.lightHandle = this.querySelector('#lightness-handle');
    this.swatches = this.querySelectorAll('.preset-swatch');

    this.initSliders();
    this.initSwatches();
    this.ensureHiddenInputExists();
    this.renderColorSystem();
  }

  ensureHiddenInputExists() {
    this.targetForm = document.getElementById(this.targetFormId) || 
                      this.closest('section')?.querySelector('form[action="/cart/add"]') ||
                      document.querySelector(`form[id^="product-form-"]`);

    if (!this.targetForm) return;

    this.hiddenInput = this.targetForm.querySelector('input[name="properties[Custom Color]"]');
    
    if (!this.hiddenInput) {
      this.hiddenInput = document.createElement('input');
      this.hiddenInput.type = 'hidden';
      this.hiddenInput.name = 'properties[Custom Color]';
      this.targetForm.appendChild(this.hiddenInput);
      
      const finalHex = this.hslToHex(this.colorState.hue, 100, this.colorState.lightness);
      this.hiddenInput.value = finalHex;
      this.updateUnderlayColor(finalHex);
    }
  }

  initImageObserver() {
    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const underlay = img.parentElement?.querySelector('.product__image-underlay');
          if (underlay) {
            this.revealUnderlayWhenReady(img, underlay);
          }
        }
      });
    }, { root: null, threshold: 0.1 });

    this.observeProductImages();
  }

  observeProductImages() {
    if (!this.imageObserver) return;

    const selectors = [
      '.product-media-container modal-opener img',
      'modal-opener img',
      '.product__media img',
      '.product__main-photos img'
    ];

    const images = document.querySelectorAll(selectors.join(', '));
    images.forEach(img => {
      if (!this.observedImages.has(img)) {
        this.observedImages.add(img);
        this.imageObserver.observe(img);

        // Immediate check if element is already in viewport and loaded
        const underlay = img.parentElement?.querySelector('.product__image-underlay');
        if (underlay && img.complete) {
          this.revealUnderlayWhenReady(img, underlay);
        }
      }
    });
  }

  revealUnderlayWhenReady(img, underlay) {
    if (img.complete && img.naturalWidth !== 0) {
      underlay.classList.add('underlay-visible');
    } else {
      img.addEventListener('load', () => {
        underlay.classList.add('underlay-visible');
      }, { once: true });
    }
  }

  hslToHex(h, s, l) {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = n => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  updateUnderlayColor(hexColor) {
    document.querySelectorAll('.product__image-underlay').forEach(underlay => {
      underlay.style.setProperty('--underlay-color', hexColor);
    });
  }

  renderColorSystem() {
    const finalHex = this.hslToHex(this.colorState.hue, 100, this.colorState.lightness);
    
    if (this.hueHandle) {
      this.hueHandle.style.left = `${(this.colorState.hue / 360) * 100}%`;
      this.hueHandle.style.backgroundColor = finalHex;
    }
    
    if (this.lightTrack) {
      this.lightTrack.style.background = `linear-gradient(to right, hsl(${this.colorState.hue}, 100%, 0%), hsl(${this.colorState.hue}, 100%, 50%), hsl(${this.colorState.hue}, 100%, 100%))`;
    }
    
    if (this.lightHandle) {
      this.lightHandle.style.left = `${this.colorState.lightness}%`;
      this.lightHandle.style.backgroundColor = finalHex;
    }
    
    if (this.hiddenInput) {
      this.hiddenInput.value = finalHex;
      this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateUnderlayColor(finalHex);
  }

  processPointerMovement(trackElement, clientX) {
    const rect = trackElement.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    return x / rect.width;
  }

  initSliders() {
    if (!this.hueTrack || !this.lightTrack) return;

    const hueInteractionArea = this.hueTrack.parentElement.classList.contains('custom-picker-container') ? this.hueTrack : this.hueTrack.parentElement;
    const lightInteractionArea = this.lightTrack.parentElement.classList.contains('custom-picker-container') ? this.lightTrack : this.lightTrack.parentElement;

    const handleHue = (e) => {
      const percentage = this.processPointerMovement(this.hueTrack, e.clientX);
      this.colorState.hue = Math.round(percentage * 360);
      this.renderColorSystem();
    };

    hueInteractionArea.addEventListener('pointerdown', (e) => {
      if (e.target.classList.contains('preset-swatch') || e.target.classList.contains('form__label')) return;
      this.activeTrack = 'hue';
      hueInteractionArea.setPointerCapture(e.pointerId);
      handleHue(e);
    });

    hueInteractionArea.addEventListener('pointermove', (e) => {
      if (this.activeTrack !== 'hue') return;
      handleHue(e);
    });

    hueInteractionArea.addEventListener('pointerup', (e) => {
      if (this.activeTrack !== 'hue') return;
      this.activeTrack = null;
      hueInteractionArea.releasePointerCapture(e.pointerId);
    });

    const handleLightness = (e) => {
      const percentage = this.processPointerMovement(this.lightTrack, e.clientX);
      this.colorState.lightness = Math.round(percentage * 100);
      this.renderColorSystem();
    };

    lightInteractionArea.addEventListener('pointerdown', (e) => {
      if (e.target.classList.contains('preset-swatch') || e.target.classList.contains('form__label')) return;
      this.activeTrack = 'lightness';
      lightInteractionArea.setPointerCapture(e.pointerId);
      handleLightness(e);
    });

    lightInteractionArea.addEventListener('pointermove', (e) => {
      if (this.activeTrack !== 'lightness') return;
      handleLightness(e);
    });

    lightInteractionArea.addEventListener('pointerup', (e) => {
      if (this.activeTrack !== 'lightness') return;
      this.activeTrack = null;
      lightInteractionArea.releasePointerCapture(e.pointerId);
    });
  }

  initSwatches() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        this.colorState.hue = parseInt(swatch.getAttribute('data-hue'), 10);
        this.colorState.lightness = parseInt(swatch.getAttribute('data-lightness'), 10);
        this.renderColorSystem();
      });
    });
  }
}

if (!customElements.get('custom-color-picker')) {
  customElements.define('custom-color-picker', CustomColorPicker);
}